import { useRef, useEffect } from "react";
import { gradingLeague, hydrateSingleLeg, betDisplayStatus, calcStatus, calcPayout, fmtOdds, hasLineValue, isNegativeLine, effectiveParlayOdds, isSettlementPendingForBet, isUntrackedLeg, isDisplayDnpLeg, readsPlayerStat, isNoBinaryLeg, isNotGradedLeague, isNotGradedLegOnFinal, notGradedLegGameId } from "../lib/betting";
import { NHL_FULL_NAMES } from "../lib/constants";
import { sportsbookForDisplay } from "../lib/sportsbooks";
import { getMarketLabel, getMarketShort, isPlayerPropMarket, isBinaryPlayerProp, isEnrichablePlayerProp, marketKey, displayMarketKey } from "../lib/markets";
import { teamLegLineText } from "../lib/leg-line-text";
import { getPlayerStatForLeague } from "../lib/stat-resolver";
import { formatPropLabel, safeOverUnder } from "../lib/format";
import { isDeepLinkableMarket } from "../lib/leg-stats-link";
import { JT, visualStateFor } from "./ui/jumbotronTokens";
import { sgpGameId, sgpMatchup, singleMarketSource } from "../lib/bet-root-display";
import { prefersReducedMotion } from "../lib/platform";
import { leagueEmoji, leagueLabel } from "../lib/league-display";
import StatusWatermark from "./ui/StatusWatermark";
import StatusPill from "./ui/StatusPill";
import LegChip from "./ui/LegChip";
import LiveScoreStrip from "./ui/LiveScoreStrip";
import BetCardFooter from "./ui/BetCardFooter";

// #791 client-descope: a missed-freeze ENRICHABLE PLAYER PROP (frozen_stat null
// on a FINAL/OFF game) is unresolvable client-side. Every grading site in this
// file — the legs-won summary and the SGP + parlay leg-row pills — routes
// through this one predicate so none fabricates a terminal verdict from a stale
// statValue / re-derived live stat (Fail-Closed pre-Codex Finding 1). Strict
// `=== null` keeps server-shaped legs (frozenStat undefined) and genuine
// 0-freezes unaffected; team and non-enrichable legs fall through to calcStatus.
// Mirrors betting.js parlayLegStatuses exactly.
// #1353 PR 3b (r5b.4 I1): every grading call here trusts the server-written DNP flag.
const TRUST_DNP = { trustDnpFlag: true };

function isMissedFreezeProp(leg, legGame, bet) {
  if (isDisplayDnpLeg(leg, gradingLeague(bet, leg, legGame))) return false; // #1353 PR 3b: DNP is not a missed freeze
  const legGameFinal = legGame?.status === "FINAL" || legGame?.status === "OFF";
  return isEnrichablePlayerProp(marketKey(leg), gradingLeague(bet, leg, legGame))
    && leg.frozenStat === null && legGameFinal;
}

// Canonical player name for a leg. player_name may be exposed under `player` or its
// `playerName` alias (rowToBet/rowToClientBet set both from player_name, but legacy/partial
// shapes can carry only one -- Codex P1). Returns a trimmed non-empty string, else "".
function legPlayerName(leg) {
  if (typeof leg?.player === "string" && leg.player.trim()) return leg.player.trim();
  if (typeof leg?.playerName === "string" && leg.playerName.trim()) return leg.playerName.trim();
  return "";
}

export default function SlipTicket({bet,games,liveStats,oddsData,haptic,onOpenPlayerStats,onRequestRemove}) {
  const isSGP = bet.slipType==="sgp";
  const isParlay = bet.slipType==="parlay";
  const isMulti = isParlay||isSGP;

  // v1c deep-link: a player-prop leg opens the Stats player page. Shared by BOTH the
  // SGP and parlay leg-render paths so activation + accessibility stay identical
  // (spec v1c-addendum §F). Returns spreadable button props for a tappable player leg,
  // or null for team legs / when no callback is wired (inert, backward-compatible).
  const legTapProps = (leg) => {
    // A leg is deep-linkable when it is a player prop by EITHER signal: the markets.js
    // registry flag OR the deep-link allowlist. The allowlist is load-bearing because
    // isPlayerPropMarket() classifies goals/nhl:goals as playerProp:false (Codex P1;
    // markets.js gap per issue-769). Team legs match neither, so they stay inert.
    const isPlayerLeg = isPlayerPropMarket(leg) || isDeepLinkableMarket(marketKey(leg));
    if (!(isPlayerLeg && onOpenPlayerStats)) return null;
    const playerName = legPlayerName(leg);
    const fire = () => { haptic?.light?.(); onOpenPlayerStats({
      name: playerName||leg.pick||"",
      gameId: leg.gameId||bet.gameId||null,
      marketKey: marketKey(leg),
      league: gradingLeague(bet, leg, null),
    }); };
    return {
      role: "button",
      tabIndex: 0,
      "aria-label": `View ${playerName||"player"} stats`,
      onClick: fire,
      onKeyDown: (e)=>{ if(e.key==="Enter"||e.key===" "){ e.preventDefault(); fire(); } },
    };
  };
  // #1682: an SGP's game comes from its legs when the root game_id is unset (every SlipSync SGP).
  // It is the CARD's game only (header, start time, score strip). Each leg's verdict keeps the
  // betDisplayStatus / cron resolution (leg.gameId only, #1782), so a leg not enriched yet
  // stays gameless.
  const sgpGid = isSGP ? sgpGameId(bet) : null;
  const game = !isMulti ? games.find(g=>String(g.id)===String(bet.gameId)) : sgpGid ? games.find(g=>String(g.id)===sgpGid) : null;
  // #1037 round 3 (Nova F1): the card status goes through the shared league authority
  // (betDisplayStatus -> gradingLeague), so a single whose resolved game disagrees with
  // the root shows no verdict here -- same answer as the pill, the pages and the cron.
  const status = betDisplayStatus(bet, games, { isMulti, ...TRUST_DNP });
  // #878: void-aware payout/odds — applied ONLY to SETTLED bets (INV-5). An in-flight
  // bet in the post-FINAL/pre-cron window must keep its bet-time combinedOdds/payout;
  // effectiveParlayOdds opts into allowPush, so calling it pre-settlement could suppress
  // a not-yet-settled ticket. For settled bets: recompute on survivors where provable;
  // indeterminate/push → suppress ('—'), never over-stating the pre-void figure (INV-1/7).
  const isSettled = !!(bet.settledAt || bet.finalResult);
  const effParlay = (isMulti && isSettled) ? effectiveParlayOdds(bet, games, TRUST_DNP) : null;
  const multiOddsSuppressed = !!effParlay && (effParlay.mode === "indeterminate" || effParlay.mode === "push");
  const oddsNum = isMulti
    ? (effParlay?.mode === "recomputed"
        ? Number(String(effParlay.odds).replace("+",""))
        : multiOddsSuppressed ? null
        : (bet.combinedOdds ? Number(String(bet.combinedOdds).replace("+","")) : null))
    : bet.odds;
  const payout = oddsNum != null ? calcPayout(Number(bet.stake)||0, oddsNum) : null;
  const multiOddsLabel = isMulti
    ? (effParlay?.mode === "recomputed"
        ? (String(effParlay.odds).match(/^[+-]/) ? effParlay.odds : `+${effParlay.odds}`)
        : multiOddsSuppressed ? "—"
        : (bet.combinedOdds != null ? (String(bet.combinedOdds).match(/^[+-]/) ? bet.combinedOdds : `+${bet.combinedOdds}`) : "—"))
    : null;
  const sb = sportsbookForDisplay(bet.sportsbook); // #1199: unknown book -> neutral, never FanDuel
  const btLabel = getMarketLabel(singleMarketSource(bet)); // #1682: a single's market lives on its leg
  // #856 single-ticket player prop: a real single bet carries market/player only inside
  // legs[] (top-level bet.pick is the O/U side, e.g. "Over"), so source the header display
  // AND the deep-link from the one leg. singleIsPlayer drives rendering (callback-agnostic);
  // singleTap drives the tap (null without a callback or for team legs). Team/legacy singles
  // fall back to the bet.pick headline unchanged.
  // Key off leg CARDINALITY, not just slipType (GPT pre-audit P1): a malformed/legacy
  // multi-leg bet whose slipType is neither sgp nor parlay must NOT be rendered or
  // deep-linked as a single (which would surface only its first leg).
  const singleLeg = (!isMulti && bet.legs?.length === 1) ? bet.legs[0] : null;
  // Require a real string player identity: a deep-link resolves by NAME, so an
  // identity-less leg (null/empty/non-string player) must fall back to the bet.pick
  // header and stay inert rather than present a dead "— Over 3.5" tappable (pre-codex
  // Fail-Closed/Security LOW cluster).
  const singleName = singleLeg ? legPlayerName(singleLeg) : "";
  const singleHasIdentity = !!singleName;
  const singleIsPlayer = !!(singleHasIdentity && (isPlayerPropMarket(singleLeg) || isDeepLinkableMarket(marketKey(singleLeg))));
  const singleTap = singleIsPlayer ? legTapProps(singleLeg) : null;
  // Never-reject PR 3 (spec §6): an untracked single shows the book's own wording.
  const singleUntracked = isUntrackedLeg(singleLeg);
  const singleHeadline = singleUntracked
    ? (singleLeg.rawSelectionText || singleLeg.pick || singleLeg.player || "—")
    : singleIsPlayer
    ? (isBinaryPlayerProp(singleLeg)
        ? (singleName || singleLeg.pick || "—")
        : formatPropLabel(singleName || "—", singleLeg.overUnder, singleLeg.line))
    // #1130 / Codex P1: compose exactly as grading does (hydrateSingleLeg: leg market
    // and pick, ROOT line and side), so the headline shows the line the bet settles on.
    : (teamLegLineText(hydrateSingleLeg(bet, null)) || bet.pick);
  // #1353 PR 3b: a DNP single says so next to its market (the pill shows push / SETTLING).
  const singleDnp = !singleUntracked && isDisplayDnpLeg(singleLeg, gradingLeague(bet, singleLeg, game));
  const singleMarketLabel = singleUntracked
    ? [singleLeg.rawMarketText, "Not tracked yet"].filter(Boolean).join(" · ")
    : [singleIsPlayer ? (getMarketLabel(singleLeg) || singleLeg.market || "") : (btLabel || bet.type), singleDnp ? "DNP" : null].filter(Boolean).join(" · ");
  const isLive = game?.status==="LIVE";
  const isFinal = isMulti
    ? (isParlay ? bet.legs?.every(l=>games.find(g=>String(g.id)===String(l.gameId))?.status==="FINAL") : game?.status==="FINAL")
    : game?.status==="FINAL";

  // Derived UI-only state: game(s) ended but settlement cron hasn't graded yet.
  // Extracted to betting.js (#1037) so the settleability gate is directly testable --
  // an NFL bet has no settlement path and must never promise one. See P1-2 there.
  const isSettlementPending = isSettlementPendingForBet({ bet, game, games, isMulti });

  // Look up current odds from The Odds API for ML single bets so line movement is visible
  const SB_API_KEY = {fd:"fanduel",dk:"draftkings",mgm:"betmgm"};
  const currentOdds = (() => {
    if (!oddsData?.length || isMulti || displayMarketKey(singleMarketSource(bet))!=="ml" || !bet.pick) return null;
    // The odds feed and NHL_FULL_NAMES are NHL only: a universal `moneyline` leg on another league
    // must not borrow an NHL line (#1682 pre-Codex).
    if (gradingLeague(bet, singleLeg, game)!=="nhl") return null;
    const fullName = NHL_FULL_NAMES[bet.pick]||bet.pick;
    const apiKey = Object.prototype.hasOwnProperty.call(SB_API_KEY, bet.sportsbook) ? SB_API_KEY[bet.sportsbook] : null;
    if (!apiKey) return null;
    const gameOdds = oddsData.find(g=>g.homeTeam===fullName||g.awayTeam===fullName);
    return gameOdds?.bookmakers?.[apiKey]?.[fullName]??null;
  })();
  const oddsShifted = currentOdds!==null && currentOdds!==Number(bet.odds);
  const oddsDelta = oddsShifted ? currentOdds-Number(bet.odds) : 0;

  // Compute live leg progress for SGP/Parlay to drive slip-level status
  const gameId = String((isSGP ? sgpGid : bet.gameId)||"");
  const players = liveStats[gameId]||[];
  // #1353 PR 2 (r5.6): nfl:anytime_td is identity-checked on the server by roster id, so the SGP
  // hit/bust indicators read only the persisted value; every other market keeps the live lookup.
  // #1353 PR 3b (stat site 1): a DNP leg has no stat, so it is never a hit or a bust.
  // #1655 B3b (P3-1): all-hit and bust mean something only when the league can be graded, so
  // an SGP with a not-graded-league leg never buzzes. Keyed on the league, not the status: a
  // LIVE slip reads pending, and a later book finalResult must not fire a stale haptic either.
  const sgpNotGraded = isSGP && (bet.legs || []).some(leg => isNotGradedLeague(gradingLeague(bet, leg, game)));
  const sgpLegStat = (leg, league) => (isDisplayDnpLeg(leg, league) ? null : marketKey(leg) === 'nfl:anytime_td'
    ? (leg.frozenStat ?? leg.statValue ?? null)
    : (league ? getPlayerStatForLeague(league, players, leg.player||leg.pick||"", marketKey(leg), null) : null));
  const allLegsHit = isSGP && !sgpNotGraded && isLive && bet.legs?.length>0 && bet.legs.every(leg=>{
    if (isUntrackedLeg(leg)) return false; // never-reject PR 3: an untracked leg is never "hit" (no haptic)
    if (!leg.gameId && !bet.gameId) return false; // #1682: a gameless leg reads no stat from the shared game
    const league = gradingLeague(bet, leg, game);
    const isPlayer = readsPlayerStat(leg, league); // #1683: nhl:goals is a player leg
    const isBinary = isBinaryPlayerProp(leg);
    if (!isPlayer) return true; // skip team bets during live — don't factor into allLegsHit
    const statVal = sgpLegStat(leg, league);
    if (statVal===null) return false;
    // #1744: a No leg is never "hit" live -- it wins only at FINAL with 0 (D2).
    if (isBinary) return !isNoBinaryLeg(leg) && statVal>0;
    if (!hasLineValue(leg.line) || isNegativeLine(leg.line)) return false; // #777 null / #1135 negative line: no real target — never a fabricated hit
    const line = Number(leg.line);
    const ou=safeOverUnder(leg.overUnder); return (ou==="Over"||ou.endsWith("+")) ? statVal>=line : statVal<=line;
  });
  const anyLegBusted = isSGP && !sgpNotGraded && (isLive||isFinal) && bet.legs?.some(leg=>{
    if (isUntrackedLeg(leg)) return false; // never-reject PR 3: an untracked leg never busts (no haptic)
    if (!leg.gameId && !bet.gameId) return false; // #1682: a gameless leg never busts off the shared game
    const isPlayer = readsPlayerStat(leg, gradingLeague(bet, leg, game)); // #1683
    if (!isPlayer) return false; // never bust on team bets during live
    // #791 (Codex P1): a missed-freeze enrichable prop is unresolvable client-side
    // — it must NOT contribute a fabricated bust (red card + haptic.error), which
    // hotspot 10a fires independently of isSettlementPending. Same predicate as the
    // summary/leg-row/verdict guards, so no surface can show a false terminal.
    if (isMissedFreezeProp(leg, game, bet)) return false;
    const league = gradingLeague(bet, leg, game);
    const statVal = sgpLegStat(leg, league);
    if (statVal===null) return false;
    const isBinary = isBinaryPlayerProp(leg);
    const ou = safeOverUnder(leg.overUnder);
    const isUnder = ou==="Under";
    const isOver = ou==="Over"||ou.endsWith("+");

    // Smart bust logic:
    // Binary props (anytime goal/point) — only bust on FINAL with 0 (no line involved)
    // #1744: a No leg busts the moment the stat moves (live or final), never on a 0.
    if (isBinary) return isNoBinaryLeg(leg) ? statVal>0 : (isFinal && statVal===0);
    // #777: non-binary props with no finite line have no target — never a fabricated bust
    // #1135: a negative line is not a real target either (settlement grades it unresolvable)
    if (!hasLineValue(leg.line) || isNegativeLine(leg.line)) return false;
    const line = Number(leg.line);
    // Under props — bust immediately mid-game when exceeded (mathematically impossible to recover)
    if (isUnder) return statVal>line;
    // Over/X+ props — only bust on FINAL when stat fell short
    if (isOver) return isFinal && statVal<line;
    return false;
  });

  const prevAllHit = useRef(false);
  const prevBusted = useRef(false);
  useEffect(()=>{
    if(allLegsHit && !prevAllHit.current) haptic.heavy();
    if(anyLegBusted && !prevBusted.current) haptic.error();
    prevAllHit.current = allLegsHit;
    prevBusted.current = anyLegBusted;
  },[allLegsHit, anyLegBusted]);

  // Option A + border revision: one visual state drives watermark/pill/border/odds.
  // allLegsHit/anyLegBusted are RETIRED as card-header/border drivers (they remain for
  // haptics + strip/chip detail only — see the haptics effect above and legChipProps below).
  const vs = visualStateFor(status);
  const reducedMotion = prefersReducedMotion();
  // Signal Scarcity (PR 3). Hotspot 10a, amended 2026-09-20: isSettlementPending owns the pill,
  // watermark, odds figure and footer (the settling identity); the BORDER and the haptics stay
  // on the sub-state for a GRADED status (won/lost/push -- "graded, being confirmed"). An
  // ungraded status under settling (unresolvable from the pre-freeze prop window, pending from
  // a missed freeze) has nothing to signal, so its border is the settling border too (D8).
  const graded = status === "won" || status === "lost" || status === "push";
  const identity = isSettlementPending ? JT.status.settling : vs;
  const outerBorder = (isSettlementPending && !graded) ? JT.status.settling.border : vs.border; // sub-state border (win=green/lose=red/close=orange)
  // The odds figure is content, not status: on a pending card it reads primary (D9).
  // #1655 B3b (P3-4): NOT GRADED carries no accent either, so its odds read primary too.
  const oddsColor = isSettlementPending ? JT.status.settling.color : (vs.key === "pending" || vs.key === "notGraded") ? JT.text.primary : vs.color;
  // TO WIN is neutral until the slip is winning. `winning` IS reachable under settling (a parlay
  // whose first-team-to-score leg has no known first goal stays [won, pending] -> "winning";
  // an SGP leg with a null gameId resolves differently in rawParlayLegStatuses), so settling
  // wins here too (pre-Codex r1 P1 on PR 3).
  const toWinColor = (status === "winning" && !isSettlementPending) ? JT.status.won.color : JT.text.primary;
  // The live glow belongs to the live identity; settling owns the card, so it never pulses.
  const liveAnim = (vs.live && !isSettlementPending && !reducedMotion) ? "stLiveGlow 1.8s ease-in-out infinite" : undefined;

  // #660: "X/Y legs won" aggregate summary for multi-leg tickets. Self-contained
  // pass that is BOTH live-aware (frozenStat ?? statValue ?? live lookup -- same
  // resolution the per-leg rows use, so the summary matches what the user sees
  // during a live game) AND void-aware (the frozenStat===null-on-FINAL sentinel,
  // mirrored from parlayLegStatuses, so voided legs leave the denominator).
  // calcStatus alone applies neither the live statValue nor the void branch.
  const legStatuses = isMulti ? (bet.legs || []).map(leg => {
    const legGameId = leg.gameId ? String(leg.gameId) : (isSGP ? String(bet.gameId || "") : "");
    const legGame = games.find(g => String(g.id) === legGameId);
    const isPlayer = readsPlayerStat(leg, gradingLeague(bet, leg, legGame)); // #1683
    const legGameFinal = legGame?.status === "FINAL" || legGame?.status === "OFF";
    // #791: a missed-freeze enrichable prop is unresolvable (not silently
    // dropped) — keeps it in the X/Y denominator. See isMissedFreezeProp.
    if (isMissedFreezeProp(leg, legGame, bet)) return "unresolvable";
    // #1037 round 3 P1: the shared league authority (root / leg / game must agree).
    const legLeague = gradingLeague(bet, leg, legGame);
    const players = liveStats[legGameId] || [];
    // #1353 PR 3b (stat site 2): a DNP leg reads no stat; calcStatus (trusted) grades it push.
    const statVal = isPlayer && !isDisplayDnpLeg(leg, legLeague)
      ? (leg.frozenStat ?? leg.statValue ?? (legLeague ? getPlayerStatForLeague(legLeague, players, leg.player || leg.pick || "", marketKey(leg), null) : null))
      : null;
    const hasLiveStat = statVal !== null && (
      (isPlayer && !leg.gameId) || legGame?.status === "LIVE" || legGameFinal || leg.frozenStat !== undefined
    );
    // #817: opt this summary recompute into push so an exact-line parlay/SGP leg
    // grades `push` and drops out of the X/Y denominator below (like `void`),
    // instead of grading `lost` and inflating the count. This is the legs-won
    // SUMMARY only — the card border/status (line 74, via betDisplayStatus) and the
    // per-leg row chips are graded separately and are unaffected by this arg.
    return calcStatus(hasLiveStat ? { ...leg, liveStatVal: statVal } : leg, legGame, legLeague, true, TRUST_DNP);
  }) : [];
  // Numerator counts live-hitting ("winning") and finalized ("won") legs so the
  // line matches the visible rows during a live game (product decision, #813).
  const legsWonCount = legStatuses.filter(s => s === "won" || s === "winning").length;
  const legsCountedTotal = legStatuses.filter(s => s !== "void" && s !== "push").length;
  // Only meaningful once a leg has real progress (a game is LIVE or FINAL).
  const legsHaveProgress = legStatuses.some(s => s === "winning" || s === "losing" || s === "won" || s === "lost" || s === "close");
  const showLegsWon = isMulti && (bet.legs?.length || 0) > 1 && legsCountedTotal > 0 && legsHaveProgress;
  const legsWonRatio = legsCountedTotal > 0 ? legsWonCount / legsCountedTotal : 0;
  // Under settling the count stays (magnitude) but the accent goes -- settling owns the identity.
  const legsWonColor = isSettlementPending ? JT.text.secondary : legsWonRatio === 1 ? JT.status.won.color : legsWonRatio === 0 ? JT.text.secondary : JT.status.live.color;

  // #767: a pre-game pending card shows the game start time ("Starts 7:10 PM")
  // instead of the system state "PENDING". game_cache exposes startTime already
  // local-formatted (useGameCacheRealtime.transformRow) and startTimeUTC (raw,
  // sortable) via the raw_data spread. Only applies when the slip status is
  // `pending` and the game(s) are still UPCOMING; parlays span multiple games so
  // the EARLIEST leg start is shown (the card first goes live then). Falls back
  // to PENDING when no matched UPCOMING game carries a startTime yet (the brief
  // post-sync / pre-enrichment window).
  // A game qualifies for the pre-game "Starts" label only when it is UPCOMING and
  // carries a formatted startTime AND a startTimeUTC that parses to a finite time.
  // Requiring startTimeUTC (not just the display string) fails closed on the raw
  // fallback ("Invalid Date" / unformatted) and the new Date(null)===epoch hole.
  // Returns { startTime, t } for sorting, or null.
  const startTimeIfPreGame = (g) => {
    if (!g || g.status !== "UPCOMING" || !g.startTime || !g.startTimeUTC) return null;
    const t = new Date(g.startTimeUTC).getTime();
    return Number.isFinite(t) ? { startTime: g.startTime, t } : null;
  };
  const pregameStartTime = (() => {
    if (status !== "pending") return null;
    if (isParlay) {
      const legs = bet.legs || [];
      if (!legs.length) return null;
      // EVERY leg must resolve to a qualifying UPCOMING game before we can name the
      // true earliest. If any leg is unmatched (still in the pre-enrichment window —
      // it could start earlier), already started, or lacks a valid start time, keep
      // PENDING: a "Starts" time off a subset could be later-than-true or leak over
      // a not-fully-pre-game card.
      const dated = legs.map(l =>
        startTimeIfPreGame(games.find(gg => String(gg.id) === String(l.gameId || "")))
      );
      if (dated.some(x => x === null)) return null;
      return dated.reduce((a, b) => (b.t < a.t ? b : a)).startTime;
    }
    // Single and SGP both resolve to one game via `game` (bet.gameId; an SGP's legs when unset).
    const one = startTimeIfPreGame(game);
    return one ? one.startTime : null;
  })();
  const pendingStartLabel = pregameStartTime ? `⏱ Starts ${pregameStartTime}` : null;

  // LIVE inset score strip (single/SGP only — a parlay's `game` is always null
  // since it spans multiple games; no single "the" score to show inline).
  const liveScoreText = (isLive && game) ? `${game.away.team} ${game.away.score} — ${game.home.score} ${game.home.team}` : null;
  const liveContextText = (isLive && game && showLegsWon) ? `${legsWonCount}/${legsCountedTotal} HIT` : null;

  // #1655 B3b: "<LEAGUE> grading is coming." plus "Final: AWY n, HOM n." for a single-game bet
  // (single or SGP). The single's game follows the not_graded rule (notGradedLegGameId);
  // a parlay spans games, so it gets no score.
  const notGradedNote = (() => {
    if (status !== "not_graded") return null;
    const leg0 = bet.legs?.[0] ?? null;
    const scoreGame = isParlay ? null
      : isSGP ? game
      : games.find(g => String(g.id) === String(notGradedLegGameId(bet, leg0) || ""));
    const lg = leagueLabel(gradingLeague(bet, leg0, scoreGame || null));
    const score = (scoreGame && Number.isFinite(scoreGame.away?.score) && Number.isFinite(scoreGame.home?.score))
      ? ` Final: ${scoreGame.away.team} ${scoreGame.away.score}, ${scoreGame.home.team} ${scoreGame.home.score}.`
      : "";
    return `${lg} grading is coming.${score}`;
  })();

  // #1138: a multi the cron settled terminal `unresolvable` names the leg(s) it could not
  // grade (durable leg_status, storage.js). Gated on the PARENT's finalResult (P2-7): a
  // historical `lost` multi that carries an `unresolvable` leg renders exactly as before.
  // Never under settling -- a settled bet is not settlement-pending.
  const settledUnresolvableMulti = isMulti && bet.finalResult === "unresolvable";
  const cantGradeLeg = (leg) => settledUnresolvableMulti && leg?.status === "unresolvable";

  // Per-leg display props for LegChip. Pure over already-resolved leg facts.
  // Mirrors the existing numeric-prop math (old :380-393,451-467) so behavior is preserved.
  function legChipProps(leg, legGame) {
    // Never-reject PR 3 (spec §6): the book's own wording and a neutral "Not tracked yet"
    // label in the right-hand slot (never truncated by a long market) -- no bar, no accent.
    // Grading already ignores the leg (isUntrackedLeg in betting.js), so no stat is read.
    if (isUntrackedLeg(leg)) {
      return {
        text: leg.rawSelectionText || leg.pick || leg.player || "—",
        marketText: leg.rawMarketText || "",
        color: JT.status.pending.color, showBar: false, pct: 0, ratio: "Not tracked yet", barGradient: null,
        tapProps: {}, ratioOdds: null,
      };
    }
    // #1353 PR 3b (r5b.1 P2-2, r5b.3 H2; stat site 3): a DNP leg is "no stat" -- never a bar, a check
    // mark or a bust, and the name-based live lookup is never reached. The label shows whatever
    // leg.status or the cached game says (status is null until FREEZE_SETTLE / the secondary pass).
    if (isDisplayDnpLeg(leg, gradingLeague(bet, leg, legGame))) {
      // [r2 P3] player-leg gate for the label only: the deep-link list covers nhl:goals, which
      // isPlayerPropMarket misses (#1683's readsPlayerStat drives the stat-reading chip below)
      const dnpPlayerLeg = isPlayerPropMarket(leg) || isDeepLinkableMarket(marketKey(leg));
      return {
        text: dnpPlayerLeg && !isBinaryPlayerProp(leg) ? formatPropLabel(leg.player||leg.playerName||"—", leg.overUnder, leg.line) : (leg.pick||leg.player||leg.playerName||"—"),
        marketText: getMarketShort(leg) || getMarketLabel(leg) || leg.market || "",
        color: JT.text.secondary, showBar: false, pct: 0, ratio: "DNP", barGradient: null,
        tapProps: legTapProps(leg), ratioOdds: null,
      };
    }
    // #1655 B3b (P2-4): a not-graded-league leg on a FINAL/OFF game has no verdict to show --
    // neutral, no bar, no stat ratio. A LIVE leg falls through and keeps its live progress.
    if (isNotGradedLegOnFinal(bet, leg, legGame ? [legGame] : [])) {
      const ngPlayerLeg = readsPlayerStat(leg, gradingLeague(bet, leg, legGame)) || isDeepLinkableMarket(marketKey(leg));
      return {
        text: ngPlayerLeg && !isBinaryPlayerProp(leg) ? formatPropLabel(leg.player||leg.playerName||"—", leg.overUnder, leg.line) : (teamLegLineText(leg) || leg.pick || leg.player || "—"),
        marketText: getMarketShort(leg) || getMarketLabel(leg) || leg.market || "",
        color: JT.text.secondary, showBar: false, pct: 0, ratio: null, barGradient: null,
        tapProps: legTapProps(leg), ratioOdds: fmtOdds(leg.odds),
      };
    }
    const isPlayer = readsPlayerStat(leg, gradingLeague(bet, leg, legGame)); // #1683
    const isBinary = isBinaryPlayerProp(leg);
    const legGameId = String(leg.gameId || bet.gameId || "");
    const players = liveStats[legGameId] || [];
    const mfProp = isMissedFreezeProp(leg, legGame, bet);
    const league = gradingLeague(bet, leg, legGame);
    const statVal = mfProp ? null : (isPlayer
      ? (leg.frozenStat ?? leg.statValue ?? (league ? getPlayerStatForLeague(league, players, leg.player||leg.pick||"", marketKey(leg), null) : null))
      : null);
    const hasLine = hasLineValue(leg.line) && !isNegativeLine(leg.line); // #1135: negative = no target, neutral chip
    // #1670: a non-binary verdict (won/close/lost colour, bar, ratio) needs a real number. An
    // undefined or hostile stat reads neutral like "no stat yet", never a graded leg. Both wire
    // paths deliver `numeric` as a JSON number (PostgREST to_json; realtime.cast to_jsonb).
    const statIsNum = typeof statVal === 'number' && Number.isFinite(statVal);
    const line = Number(leg.line) || 1;
    const hasLiveStat = statVal !== null && (
      (isPlayer && !leg.gameId) || legGame?.status==="LIVE" || legGame?.status==="FINAL" || leg.frozenStat !== undefined
    );
    // #1679: "hit" follows the leg's direction -- an Under is hit at or under its line.
    const isUnder = safeOverUnder(leg.overUnder) === "Under";
    const isOver  = hasLiveStat && statIsNum && !isBinary && hasLine && (isUnder ? statVal <= line : statVal >= line);
    // D7 (PR 3): "close" means one short while the game can still move. A frozen stat is final
    // (never close); a FINAL/OFF game is final; anything else -- LIVE, a DELAYED rain delay, an
    // unknown game (not in `games` yet, the reconnect window; a parlay leg with no gameId) --
    // keeps the live reading, so the chip does not flash red while the game or the schedule
    // catches up (pre-Codex r1 P2 on PR 3).
    const gameInPlay = leg.frozenStat == null && !(legGame?.status === "FINAL" || legGame?.status === "OFF");
    const isClose = hasLiveStat && statIsNum && !isBinary && hasLine && statVal >= line-1 && statVal < line && gameInPlay;
    const isWinBin = isBinary && !isNoBinaryLeg(leg) && hasLiveStat && statVal > 0; // #1744: a No leg's stat>0 is no hit
    const color = (isOver||isWinBin) ? JT.status.won.color : isClose ? JT.status.live.color
      : (hasLiveStat && statIsNum && !isBinary && hasLine) ? JT.status.lost.color : JT.text.secondary; // no stat yet: neutral, readable
    const showBar = hasLiveStat && statIsNum && !isBinary && hasLine;
    const pct = showBar ? Math.max(Math.min((statVal/line)*100, 100), 3) : 0;
    const barGradient = isOver ? "linear-gradient(90deg,#4ade80,#22d3ee)"
      : isClose ? "linear-gradient(90deg,#fb923c,#fbbf24)"
      : "linear-gradient(90deg,#f87171,#fb923c)";
    // #777: a null-line prop has no target — hasLiveStat alone is not sufficient
    // here (it does not check hasLine), so gate the ratio on hasLine too or this
    // renders a garbage "X/null" readout (Number(null) defaults `line` to 1 but
    // `leg.line` itself is still null in the template).
    // #460: a binary prop has THREE states, not two. "✓" is a hit; a known zero
    // and no data at all are different facts and must not share the dash --
    // "he hasn't scored" and "we lost track of this leg" looked identical on the
    // card, which is the whole bug.
    //
    // statVal is never null BECAUSE THE STAT IS ZERO. src/lib/nhl.js
    // parseBoxscorePlayers walks every dressed skater and goalie in
    // playerByGameStats and coerces `goals: p.goals || 0`, so a 0-goal player
    // resolves to 0 and the cron writes stat_value: 0 today.
    //
    // It IS null (or undefined) for three unrelated reasons, none of them "zero":
    // an unidentified player (src/lib/betting.js findPlayerMatch -- no name match,
    // an ambiguous one matching two players, an empty players array, or an empty
    // name); a market key the league's switch does not handle (`default: return
    // null`); and an
    // identified player whose stat key is unseeded in the client liveStats
    // projection, which returns `undefined` rather than null. Only the last clause
    // -- never zero -- is what this gate depends on.
    //
    // hasLiveStat IS NOT THE GATE. It looks like one and is not:
    //
    //   1. Its last disjunct is `leg.frozenStat !== undefined`, and BOTH row
    //      mappers write `frozenStat: r.frozen_stat ?? null` (src/lib/storage.js,
    //      api/import-confirm.js). For every leg loaded from Supabase that clause
    //      is unconditionally TRUE, so the LIVE/FINAL disjuncts are dead weight and
    //      hasLiveStat collapses to `statVal !== null` with no game-state gate.
    //   2. `!== null` does not exclude `undefined`. getPlayerStat returns
    //      `match.<key>`, and useLiveStats.initPlayer seeds a LEAGUE-SPECIFIC key
    //      set on whichever leg creates the player entry, so a missing key yields
    //      undefined -- which String() would render as the literal text "undefined".
    //   3. isMissedFreezeProp, the guard that exists to WITHHOLD a verdict, is
    //      gated on `isEnrichablePlayerProp(key, league)`; getLeague(null) returns
    //      EMPTY_CAPABILITIES with an empty Set, so it silently switches off for a
    //      league-null leg while isBinaryPlayerProp -- a pure string compare -- still
    //      says binary. It also needs legGame.status FINAL/OFF, and game_cache only
    //      spans yesterday/today/tomorrow (Hotspot #9), so the missed-freeze leg
    //      ages out of `games` within ~2 days and the guard fails OPEN on exactly
    //      the leg it was written for.
    //
    // Under the old two-state expression none of that mattered: anything not > 0
    // fell to "—", so a wrong value was invisible. Turning the else-branch into a
    // positive assertion of fact makes each of them user-visible, so the gate has
    // to be built from positive evidence rather than the absence of a negative.
    //
    // KNOWN means: a real, non-negative number, AND either a cron-written freeze
    // (authoritative, survives the game ageing out of game_cache) or a game we can
    // currently see as LIVE (a fresh in-game poll). Everything else -- stale
    // snapshot, missed freeze, unresolvable game, unmatched leg (Hotspot #12/#13),
    // undefined, NaN, a string, an object, a negative count -- is "—".
    //
    // BINARY MARKETS ONLY. The `>= 0` term is safe because isBinaryPlayerProp
    // admits only non-negative counts (anytime goal/point/assist). Do not reuse
    // this gate for the non-binary branch without revisiting it -- plusMinus is
    // legitimately negative.
    //
    // SCOPE, stated because the asymmetry is deliberate and not an oversight:
    // isWinBin below still uses hasLiveStat, so the "✓" branch asserts a HIT under
    // the weaker gate this comment argues against. That hole is pre-existing on
    // develop, and closing it here would be worse than leaving it: the legs-won
    // summary feeds the SAME hasLiveStat into calcStatus as liveStatVal, so
    // tightening only this chip would make the chip and the "X/Y legs won" line
    // disagree on the same card. Tightening both is a grading-display change on the
    // ratified #791/#818 surface -- Nicky's call, not this fix's. Logged as
    // carry-forward debt in docs/specs/bugs/live-slip-card-rendering.md.
    const statIsKnown =
      typeof statVal === 'number' && Number.isFinite(statVal) && statVal >= 0
      && (leg.frozenStat != null || legGame?.status === "LIVE");
    // The colour is deliberately unchanged: a known zero stays muted, not lost.
    // During a LIVE game a 0-goal player can still score, and grading is
    // calcStatus' job (#818), not this chip's.
    const ratio = isBinary ? (isWinBin ? "✓" : statIsKnown ? String(statVal) : "—")
      // #1670: the text reads the stat reached (2/1.5); the clamp is the bar's (pct) only. A
      // non-number stat renders no ratio, never "NaN/3.5" (statIsNum has no sign rule: a
      // non-binary stat such as plusMinus can be negative).
      : (hasLiveStat && hasLine && statIsNum) ? `${statVal}/${leg.line}`
      : null;
    // Text uses the same broadened player-leg gate as legTapProps' isPlayerLeg
    // (isPlayerPropMarket OR the deep-link allowlist) — isPlayerPropMarket alone
    // misclassifies goals/nhl:goals as non-player (Codex P1, markets.js gap), which
    // would otherwise render "—" instead of the player's name for a deep-linkable
    // goals prop (regression against the v1c deep-link accessible-name contract).
    const isPlayerLeg = isPlayer || isDeepLinkableMarket(marketKey(leg));
    const text = isPlayerLeg && !isBinary ? formatPropLabel(leg.player||leg.playerName||"—", leg.overUnder, leg.line)
      : isBinary ? (leg.pick||leg.player||leg.playerName||"—")
      : (teamLegLineText(leg) || leg.pick || "—"); // #1130: spread/total carry their line
    const marketText = getMarketShort(leg) || getMarketLabel(leg) || leg.market || "";
    // #1138: the leg the cron could not grade -- neutral, no bar, no stat verdict.
    if (cantGradeLeg(leg)) {
      return { text, marketText, color: JT.text.secondary, showBar: false, pct: 0, ratio: "Can't grade",
        barGradient: null, tapProps: legTapProps(leg), ratioOdds: null };
    }
    return { text, marketText, color, showBar, pct, ratio, barGradient,
      tapProps: legTapProps(leg), ratioOdds: fmtOdds(leg.odds) };
  }

  return (
    <div style={{ position:"relative", borderRadius:JT.radius.card, overflow:"hidden",
      marginBottom:12, background:JT.bg.card, border:`1.5px solid ${outerBorder}`,
      boxShadow:"none", animation:liveAnim, transition:"border-color .4s ease" }}>
      <StatusWatermark label={isSettlementPending ? "SETTLING" : vs.label} color={identity.color} />
      {/* Row 1: status pill + sportsbook */}
      <div style={{ position:"relative", display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 14px 0" }}>
        <StatusPill
          label={isSettlementPending ? "SETTLING" : (pendingStartLabel || vs.label)}
          color={identity.color}
          dim={identity.dim}
          live={vs.live && !isSettlementPending}
          reducedMotion={reducedMotion}
        />
        {/* #1214 PR B (spec §16.2): the right side is a group -- sportsbook name + the ⋯ "Bet options"
            button, rendered only when the page passes onRequestRemove (never for guests). The 44pt
            hit area uses negative margins so the header row keeps its height. */}
        <div style={{ display:"flex", alignItems:"center", gap:4 }}>
          <span style={{ fontSize:11, fontWeight:800, color:((Object.prototype.hasOwnProperty.call(JT.book, bet.sportsbook) && JT.book[bet.sportsbook])||sb.color||JT.text.secondary) }}>{sb.name}</span>
          {onRequestRemove && (
            <button type="button" aria-label="Bet options" onClick={(e)=>{ e.stopPropagation(); onRequestRemove(bet); }}
              style={{ minWidth:44, minHeight:44, margin:"-14px -12px -14px 0", padding:0, display:"flex", alignItems:"center", justifyContent:"center", background:"none", border:"none", color:JT.text.tertiary, fontSize:18, lineHeight:1, cursor:"pointer", fontFamily:"inherit" }}>
              <span aria-hidden="true">⋯</span>
            </button>
          )}
        </div>
      </div>
      <div style={{ position:"relative", padding:"10px 14px 14px" }}>

        {/* Multi-leg ticket (SGP or Parlay) */}
        {isMulti && (
          <>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <span style={{ fontSize:13, fontWeight:800, color:JT.text.primary }}>{isSGP ? (sgpMatchup(bet, game)||"—") : `${bet.legs.length}-Leg Parlay`}</span>
              <span data-testid="multi-odds" style={{ ...JT.display900, fontSize:18, color:oddsColor }}>{multiOddsLabel}</span>
            </div>
            <LiveScoreStrip scoreText={liveScoreText} contextText={liveContextText} />
            {bet.legs.map((leg,i) => {
              const g = games.find(x=>String(x.id)===String(leg.gameId||(isSGP?bet.gameId:"")));
              const p = legChipProps(leg, g);
              return <LegChip key={i} text={p.text} marketText={p.marketText} color={p.color}
                tappable={!!p.tapProps} onTap={p.tapProps?.onClick}
                showBar={p.showBar} pct={p.pct} ratio={p.ratio ?? p.ratioOdds} barGradient={p.barGradient} />;
            })}
          </>
        )}

        {/* Single ticket */}
        {!isMulti && (
          <>
            <div style={{ marginBottom:10 }}>
              <div style={{ fontSize:11, color:JT.text.tertiary, marginBottom:4 }}>{leagueEmoji(bet.league)} {bet.teams}</div>
              <div style={{ ...JT.display900, fontSize:20, color:JT.text.primary, lineHeight:1.1, cursor:singleTap?"pointer":undefined, minHeight:singleTap?44:undefined }} {...(singleTap||{})}>{singleHeadline}</div>
              <div style={{ fontSize:11, color:JT.text.tertiary, marginTop:3 }}>{singleMarketLabel}</div>
              {oddsShifted && (
                <div style={{ fontSize:11, marginTop:4, color: oddsDelta>0 ? JT.status.won.color : JT.status.lost.color, fontWeight:700 }}>
                  {fmtOdds(currentOdds)} now {oddsDelta>0?"↑":"↓"}
                </div>
              )}
            </div>
            <LiveScoreStrip scoreText={liveScoreText} contextText={liveContextText} />
          </>
        )}

        {isSettlementPending && (
          <div style={{ fontSize:11, color:JT.status.settling.color, background:`${JT.status.settling.color}0f`, border:`1px solid ${JT.status.settling.color}26`, borderRadius:8, padding:"8px 10px", marginTop:8, lineHeight:1.5 }}>
            Final score is in. SlipTrack is checking your legs and will move this to Results once graded.
          </div>
        )}

        {/* #1655 B3b: why a NOT GRADED slip has no verdict, plus the final score for one game */}
        {status === "not_graded" && (
          <div data-testid="not-graded-note" style={{ fontSize:11, color:JT.text.secondary, marginTop:8, lineHeight:1.5 }}>
            {notGradedNote}
          </div>
        )}

        {/* Never-reject PR 3: what an unresolvable slip is waiting on (never under settling) */}
        {(() => {
          const n = (bet.legs || []).filter(isUntrackedLeg).length;
          return status === "unresolvable" && n > 0 && !isSettlementPending ? (
            <div style={{ fontSize:11, color:JT.text.secondary, marginTop:8, lineHeight:1.5 }}>
              Waiting on {n} untracked {n === 1 ? "leg" : "legs"}
            </div>
          ) : null;
        })()}

        {/* #1138: which leg(s) a settled unresolvable multi could not grade (neutral, like the untracked note) */}
        {(() => {
          const legs = settledUnresolvableMulti ? (bet.legs || []).filter(cantGradeLeg) : [];
          if (!legs.length) return null;
          const label = legs.length === 1
            ? (() => {
                const leg = legs[0];
                const p = legChipProps(leg, games.find(x => String(x.id) === String(leg.gameId || (isSGP ? bet.gameId : ""))));
                return [p.text, p.marketText].filter(Boolean).join(" ") || "this leg";
              })()
            : `${legs.length} legs`;
          return (
            <div style={{ fontSize:11, color:JT.text.secondary, marginTop:8, lineHeight:1.5 }}>
              We can't grade {label} — check your book for the result.
            </div>
          );
        })()}

        {/* Legacy incomplete leg disclaimer */}
        {isMulti && bet.finalResult === 'lost' && bet.settledAt && bet.settledAt < new Date('2026-04-29T00:00:00Z').getTime() && bet.legs?.some(l => isPlayerPropMarket(l)) && bet.legs.filter(l => isPlayerPropMarket(l)).every(l => l.frozenStat == null) && (
          <div style={{fontSize:11,color:JT.text.tertiary,fontStyle:"italic",padding:"6px 0 2px",lineHeight:1.4}}>
            Some player-prop leg results may be incomplete for this older settled slip. The overall result is accurate.
          </div>
        )}

        {/* #660: X/Y legs won summary (multi-leg tickets, above the divider) */}
        {showLegsWon && (
          <div style={{textAlign:"center",fontSize:11,fontWeight:700,color:legsWonColor,marginTop:8,letterSpacing:"0.3px"}}>
            {legsWonCount}/{legsCountedTotal} legs won
          </div>
        )}

        {/* perforated divider */}
        <div style={{ borderTop:`1.5px dashed ${JT.border.mid}`, margin:"12px -14px", position:"relative" }}>
          <div style={{ position:"absolute", left:-8, top:-8, width:16, height:16, borderRadius:"50%", background:JT.bg.root }}/>
          <div style={{ position:"absolute", right:-8, top:-8, width:16, height:16, borderRadius:"50%", background:JT.bg.root }}/>
        </div>
        <BetCardFooter
          stake={`$${Number(bet.stake)||0}`}
          odds={isMulti ? multiOddsLabel : fmtOdds(bet.odds)}
          toWin={payout!=null ? `$${payout}` : "—"}
          result={bet.finalResult==='won' ? (payout!=null ? `$${payout} WON` : "WON") : bet.finalResult==='lost' ? "LOST" : null}
          color={oddsColor}
          toWinColor={toWinColor}
        />
      </div>
    </div>
  );
}
